import Backoffice from "@snark/backoffice"
import Vue from "vue"

const context = {
	account: Backoffice.populator("account", {}),
	building: Backoffice.populator("building", {}),
	vlbox: Backoffice.populator("vlbox", {}),
};

async function getAccountOptions() {
	const accounts = await Vue.backoffice.requestor.get("/account");
	return (Array.isArray(accounts) ? accounts : [])
		.map(account => ({ value: account._id, label: account.name || account._id }))
		.sort((a, b) => a.label.localeCompare(b.label));
}

async function getBuildingOptions(input) {
	const accountId = typeof input === "string" ? input : input?.object?.accountId;
	if (!accountId) return [];

	const buildings = await Vue.backoffice.requestor.get("/building", null, { accountId });
	return (Array.isArray(buildings) ? buildings : [])
		.filter(building => building.accountId?.toString?.() === accountId.toString())
		.map(building => ({ value: building._id, label: building.name || building._id }))
		.sort((a, b) => a.label.localeCompare(b.label));
}

async function getVlboxOptions(input) {
	const buildingId = typeof input === "string" ? input : input?.object?.buildingId;
	if (!buildingId) return [];

	await context.vlbox.load();
	const items = context.vlbox.items || [];
	return items
		.filter(v => v && String(v.buildingId) === String(buildingId))
		.map(v => ({ value: v._id, label: `${v.name} (${v.model || v.brand || v._id})` }))
		.sort((a, b) => a.label.localeCompare(b.label));
}

function cleanItem(item) {
	const clean = { ...item, type: "camera" };
	delete clean.account;
	delete clean.building;
	delete clean.projectorStatus;
	delete clean.vlboxPopulated;
	delete clean.lastSeenAt;
	delete clean.statusGraceMs;
	delete clean.createdAt;
	delete clean.updatedAt;
	return clean;
}

export default {
	name: "camera",
	collection: "projector",
	idProperty: "_id",
	section: "devices",
	backPopulateList: ["account", "building"],
	filterList: function(items) {
		return Array.isArray(items) ? items.filter(item => item.type === "camera") : items;
	},
	labels: { singleTitle: "Caméra", listTitle: "Caméras" },
	emptyItem: { type: "camera", accountId: null, buildingId: null, name: "", vlboxId: null, adminInterface: "" },
	fields: [
		{ name: "_id", label: "Id", readOnly: true, hideOnCreate: true },
		{ name: "type", label: "Type", readOnly: true, formatter: Backoffice.formatters.mapping({ camera: "Caméra" }) },
		{ name: "name", label: "Nom" },
		{
			name: "accountId",
			label: "Account",
			formatter: Backoffice.formatters.linkObjectId("account", { idProperty: "_id", label: context.account.formatter() }),
			editor: {
				type: "select",
				options: getAccountOptions,
				transform: (value, form) => {
					if (form.accountId !== value) { form.buildingId = null; form.vlboxId = null; }
					return value;
				},
			},
		},
		{
			name: "buildingId",
			label: "Building",
			formatter: Backoffice.formatters.linkObjectId("building", { idProperty: "_id", label: context.building.formatter() }),
			editor: {
				type: "select",
				options: getBuildingOptions,
				watch: "accountId",
				transform: (value, form) => {
					if (form.buildingId !== value) form.vlboxId = null;
					return value;
				},
			},
		},
		{
			name: "vlboxId",
			label: "VLBox associée (caméra USB)",
			formatter: Backoffice.formatters.linkObjectId("vlbox", { idProperty: "_id", label: context.vlbox.formatter() }),
			editor: {
				type: "select",
				options: getVlboxOptions,
				watch: "buildingId",
			},
		},
		{ name: "adminInterface", label: "Adresse d'accès (auto-remplie via Cloudflare)" },
	],
	listColumns: [
		{ name: "name", label: "Nom" },
		{ name: "account", label: "Account", formatter: Backoffice.formatters.subProperty("name"), nosingle: true },
		{ name: "building", label: "Building", formatter: Backoffice.formatters.subProperty("name"), nosingle: true },
		{ name: "adminInterface", label: "Adresse d'accès" },
	],
	loadSingle() {
		return Promise.all([context.account.load(), context.building.load(), context.vlbox.load()]);
	},
	beforeSave(item) {
		return Promise.resolve(cleanItem(item));
	}
};
